import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useGetTrendingTags } from '@onesource/services';
import type { FC } from 'react';

import { TrendingTagsWidgetSkeleton } from './trending-tags-widget-skeleton';
import { WidgetCard, WidgetEmpty } from './widget-card';

interface TrendingTagsWidgetProps {
  spaceId: string;
  /** Currently applied feed tag-filter values, to highlight active tags. */
  selectedTagValues?: string[];
  /** Toggles the tag in the feed's tag filter. */
  onTagClick: (value: string) => void;
}

/**
 * Top tags used in the space recently. Clicking a tag applies / removes it as a
 * feed filter. Always renders the card; shows an empty state when nothing is
 * trending yet.
 */
export const TrendingTagsWidget: FC<TrendingTagsWidgetProps> = ({ spaceId, selectedTagValues, onTagClick }) => {
  const { data, isLoading } = useGetTrendingTags(spaceId);

  if (isLoading) {
    return <TrendingTagsWidgetSkeleton />;
  }

  const tags = data?.tags ?? [];
  if (tags.length === 0) {
    return (
      <WidgetCard title="Trending Tags">
        <WidgetEmpty>No trending tags yet — tag your posts to get started.</WidgetEmpty>
      </WidgetCard>
    );
  }

  return (
    <WidgetCard title="Trending Tags">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {tags.map(tag => {
          const isSelected = selectedTagValues?.includes(tag.value) ?? false;
          return (
            <Chip
              key={tag.value}
              label={`#${tag.label} · ${tag.count}`}
              size="small"
              onClick={() => onTagClick(tag.value)}
              sx={{
                height: 24,
                borderRadius: '12px',
                bgcolor: theme => (isSelected ? theme.palette.primary.main : `${theme.palette.secondary.main}29`),
                color: theme => (isSelected ? theme.palette.common.white : theme.palette.text.primary),
                fontFamily: 'Figtree, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: theme => (isSelected ? theme.palette.primary.dark : `${theme.palette.secondary.main}44`),
                },
                '& .MuiChip-label': { px: '10px' },
              }}
            />
          );
        })}
      </Box>
    </WidgetCard>
  );
};
